import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import DOMPurify from 'dompurify';

type LogDetailPopupProps = {
    data: any;
    setShowModal: (value: boolean) => void;
    showModal: boolean;
};

export const EmailLogDetailPopup = ({
    data,
    setShowModal,
    showModal
}: LogDetailPopupProps) => {
    const navigate = useNavigate();

    const closeModal = () => {
        setShowModal(false);
    }

    const formatDate = (value?: string | Date | null) => {
        if (!value) return "No date";

        try {
            return format(new Date(value), "MM/dd/yyyy hh:mm a");
        } catch {
            return "No date";
        }
    }

    const getAttachments = () => {
        if (!data?.attachments) {
            return [];
        }

        if (Array.isArray(data.attachments)) {
            return data.attachments;
        }

        if (typeof data.attachments === "string") {
            try {
                const parsed = JSON.parse(data.attachments);

                if (Array.isArray(parsed)) {
                    return parsed;
                }

                return [parsed];
            } catch {
                return data.attachments
                    .split(",")
                    .map((item: string) => item.trim())
                    .filter(Boolean);
            }
        }

        return [];
    }

    const attachments = getAttachments();

    const getAttachmentName = (attachment: any) => {
        if (typeof attachment === "string") {
            return attachment.split("/").pop()?.replace("Get?name=", "") || attachment;
        }
    }

    const getAttachmentUrl = (attachment: any) => {
        if (typeof attachment === "string") {
            return attachment.startsWith("http")
                ? attachment
                : null;
        }
    }

    const handleEntityNavigation = () => {
        if (!data?.entityId) {
            return;
        }

        if (data.entityType === "Leadership") {
            navigate(`/admin/leadership/editLeadership/${data.entityId}`);
            return;
        }

        navigate(`/admin/members/editMember/${data.entityId}`);
    }

    return (
        <div className={`fixed inset-0 p-4 ${showModal ? "flex" : "hidden"} justify-center items-center w-full h-full z-[1000] bg-black bg-opacity-50`}>
            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white shadow-lg rounded-md px-5 py-4 relative">
                <button
                    type="button"
                    onClick={closeModal}
                    className="absolute right-5 top-5 text-gray-500 hover:text-red-500"
                    aria-label="Close"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        viewBox="0 0 320.591 320.591"
                        fill="currentColor"
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                </button>

                <div className="my-2">
                    <div className="text-center mb-6">
                        <h4 className="text-xl text-secondary font-semibold">
                            Email Log Detail
                        </h4>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                To
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                {data?.toEmail || "-"}
                            </span>
                        </div>

                        {
                            data?.replyTo && (
                                <div className="w-full text-sm">
                                    <label className="block text-secondary text-xs">
                                        Reply To
                                    </label>

                                    <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                        {data?.replyTo?.replace(/[[\]"]/g, "")}
                                    </span>
                                </div>
                            )
                        }
                    </div>

                    {
                        (data?.cc || data?.bcc) && (
                            <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                                <div className="w-full text-sm">
                                    <label className="block text-secondary text-xs">
                                        CC
                                    </label>

                                    <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                        {data?.cc?.replace(/[[\]"]/g, "") || "-"}
                                    </span>
                                </div>

                                <div className="w-full text-sm">
                                    <label className="block text-secondary text-xs">
                                        BCC
                                    </label>

                                    <span className="w-full text-sm text-black mt-2 inline-block text-left break-all">
                                        {data?.bcc?.replace(/[[\]"]/g, "") || "-"}
                                    </span>
                                </div>
                            </div>
                        )
                    }

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                {data?.entityType === "Leadership" ? "Leadership Name" : "Member Name"}
                            </label>

                            {
                                data?.entityId ? (
                                    <button
                                        type="button"
                                        onClick={handleEntityNavigation}
                                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                        {data?.entityName || "-"}
                                    </button>
                                ) : (
                                    <span className="w-full text-sm text-black mt-2 inline-block">
                                        {data?.entityName || "All"}
                                    </span>
                                )
                            }
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                {data?.entityType === "Leadership" ? "Leadership Team Name" : "Ministry Name"}
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block">
                                {data?.entityTypeName || (data?.entityType === "Leadership" ? "All Leadership" : "All Member")}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Status
                            </label>

                            <span
                                className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${data?.status === "Sent"
                                    ? "bg-green-100 text-green-700"
                                    : data?.status === "Delivered"
                                        ? "bg-blue-100 text-blue-700"
                                        : data?.status === "Failed"
                                            ? "bg-red-100 text-red-700"
                                            : data?.status === "Pending"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : "bg-gray-100 text-gray-700"
                                    }`}
                            >
                                {data?.status || "-"}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xm:grid-cols-2 gap-4 my-4">
                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Created By
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block break-all">
                                {data?.createdBy || "-"}
                            </span>
                        </div>

                        <div className="w-full text-sm">
                            <label className="block text-secondary text-xs">
                                Sent At
                            </label>

                            <span className="w-full text-sm text-black mt-2 inline-block">
                                {formatDate(data?.sentAt)}
                            </span>
                        </div>
                    </div>

                    <div className="my-4">
                        <label className="block text-secondary text-xs">
                            Created At
                        </label>

                        <span className="w-full text-sm text-black mt-2 inline-block">
                            {formatDate(data?.createdAt)}
                        </span>
                    </div>

                    <div className="my-5">
                        <label className="block text-secondary text-xs mb-2">
                            Subject
                        </label>

                        <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-black text-left break-words">
                            {data?.subject || "No subject"}
                        </div>
                    </div>

                    <div className="my-5">
                        <label className="block text-secondary text-xs mb-2">
                            Email Body
                        </label>

                        <div
                            className="w-full max-h-[300px] overflow-y-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-black text-left break-words"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(data?.body || "No email body")
                            }}
                        />
                    </div>

                    <div className="my-5">
                        <label className="block text-secondary text-xs mb-2">
                            Attachments
                        </label>

                        {
                            attachments.length > 0
                                ? (
                                    <div className="border border-gray-200 rounded-md bg-gray-50 divide-y divide-gray-200">
                                        {
                                            attachments.map((attachment: any, index: number) => {
                                                const name = getAttachmentName(attachment);

                                                const url = getAttachmentUrl(attachment);

                                                return (
                                                    <div
                                                        key={index}
                                                        className="flex items-center justify-between gap-3 px-3 py-3"
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-9 h-9 shrink-0 rounded-md bg-blue-100 flex items-center justify-center">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="w-5 h-5 text-blue-600"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={2}
                                                                        d="M15.172 7l-6.586 6.586a2 2 0 101.414 1.414L16 9.414a4 4 0 10-5.657-5.657l-6.586 6.586a6 6 0 108.485 8.485L19.5 12"
                                                                    />
                                                                </svg>
                                                            </div>

                                                            <span className="text-sm text-gray-700 truncate">
                                                                {name}
                                                            </span>
                                                        </div>

                                                        {
                                                            url ? (
                                                                <a
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="shrink-0 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                                                >
                                                                    View
                                                                </a>
                                                            ) : (
                                                                <span className="text-xs text-gray-400">
                                                                    No URL
                                                                </span>
                                                            )
                                                        }
                                                    </div>
                                                );
                                            }
                                            )
                                        }
                                    </div>
                                ) : (
                                    <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
                                        No attachments
                                    </div>
                                )
                        }
                    </div>

                    {
                        data?.errorMessage && (
                            <div className="my-5">
                                <label className="block text-red-600 text-xs mb-2">
                                    Error Message
                                </label>

                                <div className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 text-left whitespace-pre-wrap break-words">
                                    {data.errorMessage}
                                </div>
                            </div>
                        )
                    }

                    <div className="flex max-xm:flex-col gap-4 mt-6">
                        <button
                            type="button"
                            className="px-6 py-2 w-full rounded text-black text-sm font-semibold bg-gray-200 hover:bg-gray-300 active:bg-gray-200"
                            onClick={closeModal}
                        >
                            Close
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};
